import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Trash2, Mail, Bell, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              data-testid="back-button"
              variant="ghost"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              Terms & Conditions
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Terms & Conditions
            </h2>
            <p className="text-slate-600">
              Last updated: 3rd February 2026
            </p>
          </div>

          {/* Terms Cards */}
          <div className="space-y-6">

            {/* AI Processing */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    AI-Powered Analysis
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    By using Kodo, you agree that your input (responses to activities) will be sent to a Large Language Model (LLM) for analysis. This is how we generate your personalised insights and recommendations. Your data is processed securely and used solely to provide you with the Kodo service.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Storage */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Database className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Data Storage
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We will store your data for <strong>1 year</strong> from your last activity. After this period, your data will be automatically deleted from our systems. This includes your account information, activity responses, and AI-generated insights.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Your Privacy
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    <strong>No one else will have access to your data.</strong> Your responses and insights are private to you. We do not share, sell, or provide access to your personal data to any third parties. Your career journey is yours alone.
                  </p>
                </div>
              </div>
            </div>

            {/* Email Communications */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-cyan-100 rounded-xl">
                  <Bell className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Email Communications
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    By signing up, you agree to receive emails from Kodo (e.g., weekly activities and programme updates). <strong>No spam.</strong> You can unsubscribe at any time by clicking the link in any email or contacting us.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Deletion */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Trash2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Request Data Deletion
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Want your data deleted sooner? No problem. Simply contact us at{' '}
                    <a 
                      href="mailto:hello@getkodo.co.uk" 
                      className="text-primary font-semibold hover:underline"
                    >
                      hello@getkodo.co.uk
                    </a>
                    {' '}and we'll remove all your data within 30 days.
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Cookie className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Cookies
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    We use cookies to analyse how you use our site and improve your experience. Specifically, we use:
                  </p>
                  <ul className="text-slate-600 space-y-2 mb-3">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Google Analytics cookies</strong> — to understand how visitors interact with our site (e.g., which pages are visited, how long users spend on the site)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Local storage</strong> — to keep you logged in and remember your preferences (this is not a cookie, but similar technology)</span>
                    </li>
                  </ul>
                  <p className="text-slate-600 leading-relaxed">
                    When you first visit Kodo, you'll be asked whether you accept cookies. If you decline, we won't track your activity with Google Analytics. You can change your preference at any time by clearing your browser data.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Additional Legal Sections */}
          <div className="mt-8 space-y-6">
            <h3 className="font-heading text-xl font-bold text-slate-900">Additional Terms</h3>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-6">
              {/* Age Requirement */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Age Requirement</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  You must be at least 18 years old to use Kodo. By using our service, you confirm that you meet this age requirement.
                </p>
              </div>

              {/* Disclaimer */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Disclaimer</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Kodo provides career guidance and self-reflection tools. It is not a substitute for professional mental health support, therapy, or medical advice. If you're experiencing a mental health crisis, please contact a qualified professional or your local emergency services.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Limitation of Liability</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Kodo is provided "as is" without warranties of any kind. We are not liable for any decisions you make based on insights generated by our service. Career decisions remain your personal responsibility.
                </p>
              </div>

              {/* Changes to Terms */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Changes to Terms</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We may update these terms from time to time. If we make significant changes, we'll notify you by email. Continued use of Kodo after changes constitutes acceptance of the new terms.
                </p>
              </div>

              {/* Governing Law */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Governing Law</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
            <Mail className="w-10 h-10 mx-auto mb-4 opacity-80" />
            <h3 className="font-heading text-xl font-bold mb-2">
              Questions about our terms?
            </h3>
            <p className="text-blue-100 mb-4">
              We're happy to explain anything in more detail.
            </p>
            <a 
              href="mailto:hello@getkodo.co.uk"
              className="inline-block px-6 py-3 bg-white text-primary font-semibold rounded-full hover:bg-blue-50 transition-colors"
            >
              hello@getkodo.co.uk
            </a>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-slate-500 hover:text-slate-700"
            >
              ← Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
